import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import NationalDashboardShell from '../components/NationalDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, rowMatches, isScopeSuppressed} from '../../DashboardCommon/dashboardCards';
import {NAVY, PageHeader} from '../components/widgets.comp';
import {familyCodeOf} from '../../DashboardCommon/priorityFamilies';
import {priorityRows, PriorityTable, SUPPRESSED_EMPTY} from '../components/PriorityTable.comp';

/* Slide 39 — Recommended System Interventions (National). Live rows: ND.S_16.C_2
   (PriorityTableCard). Same Region → School hierarchy as the Learning Program
   page; no Take Action (system interventions have none on any dashboard).
   "Standard Action Families" are the families the card itself lists
   (filters.action_family) — only those its rows use. Selecting one filters the
   table to the rows in that family: each row carries its family codes
   (row_meta.families). Nothing selected shows every row. */
const PRIORITY_ACTIONS_SI_SECTION_CODE = 'S_16';
const CARD_SECTIONS = [PRIORITY_ACTIONS_SI_SECTION_CODE];
const RECOMMENDATION_HEADER = 'Recommended Actions';

const FamilyFilterChip = ({label, active, onClick}) => (
    <Box
        onClick={onClick}
        sx={{
            cursor: 'pointer', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap',
            px: 1.75, py: 0.75, borderRadius: '8px',
            border: `1px solid ${active ? NAVY : 'var(--brand-border, #E5E7EB)'}`,
            backgroundColor: active ? NAVY : 'var(--brand-surface, #ffffff)',
            color: active ? '#ffffff' : NAVY,
            '&:hover': {borderColor: NAVY},
        }}
    >
        {label}
    </Box>
);

const PriorityActionsSystemInterventionPage = () => {
    const [activeFamily, setActiveFamily] = useState(null);
    const [query, setQuery] = useState('');
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const card = findCard(dashboardData, PRIORITY_ACTIONS_SI_SECTION_CODE, 'C_2');
    const ACTION_FAMILIES = card?.filters?.action_family || [];
    const rows = priorityRows(card, RECOMMENDATION_HEADER);
    const q = query.trim().toLowerCase();
    const familyCode = activeFamily ? familyCodeOf(activeFamily) : null;
    // Region / School are header filters now (QA 9 Oct); the family chips and the search box stay.
    const shown = rows
        .filter((r, i) => !familyCode || (card?.row_meta?.[i]?.families || []).includes(familyCode))
        .filter((r) => rowMatches(r, q));

    if (loading) {
        return (
            <NationalDashboardShell>
                <CircularLoader />
            </NationalDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <NationalDashboardShell>
                <NoDataAvailable />
            </NationalDashboardShell>
        );
    }

    return (
        <NationalDashboardShell>
            <PageHeader
                title="Recommended System Interventions"
                subtitle="The crosswalk converts priority COS areas into standard action families and concrete interventions."
            />

            {ACTION_FAMILIES.length > 0 && (
                <>
                    <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>
                        Standard Action Families
                    </Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 3}}>
                        {ACTION_FAMILIES.map((f) => (
                            <FamilyFilterChip key={f} label={f} active={activeFamily === f} onClick={() => setActiveFamily(activeFamily === f ? null : f)}/>
                        ))}
                    </Box>
                </>
            )}

            <PriorityTable rows={shown} recommendationHeader={RECOMMENDATION_HEADER} query={query} onQuery={setQuery} emptyText={isScopeSuppressed(dashboardData) ? SUPPRESSED_EMPTY : undefined}/>
        </NationalDashboardShell>
    );
};

export default PriorityActionsSystemInterventionPage;
